// ---------------------------------------------------------------------------
// AgentDetailView.reducer — view-local UI state for the agent detail pane.
// Consolidates the previously scattered useState flags (edit/read-only mode,
// delete confirmation, version panel visibility, and the version snapshot being
// previewed) into one reducer so related transitions live in one place. This is
// pure UI state only — the agent itself comes from the `useAgent` hook.
// ---------------------------------------------------------------------------

import type { AgentVersion } from '../../../types/agent.js';

export type AgentFormMode = 'view' | 'edit';

// Top-level tabs for the agent detail pane. Mirrors the Grader / Judge Factory
// detail views (Overview + extra tabs) — here Overview holds the form +
// version history, Evaluate holds the in-place evaluation runner.
export type AgentDetailTab = 'overview' | 'evaluate';

export interface AgentDetailViewState {
  detailTab: AgentDetailTab;
  formMode: AgentFormMode;
  deleteOpen: boolean;
  showVersions: boolean;
  viewedVersion: AgentVersion | null;
}

export type AgentDetailViewAction =
  // Re-selecting an agent resets the pane back to its default read-only state.
  | { type: 'RESET' }
  | { type: 'SET_DETAIL_TAB'; tab: AgentDetailTab }
  | { type: 'EDIT' }
  | { type: 'CANCEL_EDIT' }
  | { type: 'SET_DELETE_OPEN'; open: boolean }
  | { type: 'TOGGLE_VERSIONS' }
  | { type: 'HIDE_VERSIONS' }
  | { type: 'VIEW_VERSION'; version: AgentVersion }
  | { type: 'CLOSE_VERSION' };

export const initialAgentDetailViewState: AgentDetailViewState = {
  detailTab: 'overview',
  formMode: 'view',
  deleteOpen: false,
  showVersions: false,
  viewedVersion: null,
};

export function agentDetailViewReducer(
  state: AgentDetailViewState,
  action: AgentDetailViewAction,
): AgentDetailViewState {
  switch (action.type) {
    case 'RESET':
      return initialAgentDetailViewState;
    case 'SET_DETAIL_TAB':
      return { ...state, detailTab: action.tab };
    case 'EDIT':
      return { ...state, formMode: 'edit' };
    case 'CANCEL_EDIT':
      return { ...state, formMode: 'view' };
    case 'SET_DELETE_OPEN':
      return { ...state, deleteOpen: action.open };
    case 'TOGGLE_VERSIONS':
      return { ...state, showVersions: !state.showVersions };
    case 'HIDE_VERSIONS':
      return { ...state, showVersions: false };
    case 'VIEW_VERSION':
      return { ...state, viewedVersion: action.version };
    case 'CLOSE_VERSION':
      return { ...state, viewedVersion: null };
    default:
      return state;
  }
}
